// Copyright 2022 - 2023 Fewcha. All rights reserved.

import { createAsyncThunk } from "@reduxjs/toolkit";
import { defaultConfigReducer, TokenType } from "features/config/types";
import { getTokens } from "features/tokens/tokensReducer";
import { isCorrectBlockchain, isCorrectNetwork, myToast } from "services/utils";
import { web3 } from "services/web3";
import { getReferralCode } from "./referral";
import { mint } from "./walletReducer";

export const connect = createAsyncThunk("wallet/connect", async (token: TokenType, { dispatch }) => {
  const emptyConnect = {
    address: "",
    publicKey: "",
    isConnected: false,
    networkName: "",
    networkUrl: "",
    networkType: "",
    validNetwork: false,
  };

  if (!(window as any).fewcha) {
    window.open("https://chrome.google.com/webstore/detail/fewcha-wallet/ebfidpplhabeedpnhjnobghokpiioolj", "_blank");

    return emptyConnect;
  }

  const { data, status } = await web3.action.connect();

  switch (status) {
    case 200:
      const { address, publicKey } = data;
      dispatch(getReferralCode(address));
      myToast("Connect successfully", "success", 2000);
      const network = await getNetworkRaw();

      if (token.tier) {
        dispatch(
          mint({
            token,
            address,
            publicKey,
            validNetwork: network.validNetwork,
          }),
        );
      }

      return {
        address,
        publicKey,
        isConnected: true,
        networkName: network.networkName,
        networkUrl: network.networkUrl,
        networkType: network.networkType,
        validNetwork: network.validNetwork,
      };

    case 401:
      myToast("Connect has been rejected", "error", 2000);

      break;
    default:
      break;
  }

  return emptyConnect;
});

export const getAccount = createAsyncThunk("wallet/getAccount", async (_, { dispatch }) => {
  const { data, status } = await web3.action.account();

  if (status === 200) {
    const { address, publicKey } = data;

    dispatch(getNetwork());
    dispatch(getBalance());
    dispatch(getTokens());
    dispatch(getReferralCode(address));
    return { address, publicKey, isConnected: true };
  }

  return { address: "", publicKey: "", isConnected: false };
});

export const getBalance = createAsyncThunk("wallet/getBalance", async () => {
  const { data: balance } = await web3.action.getBalance();
  return { balance };
});

export const disconnect = createAsyncThunk("wallet/disconnect", async () => {
  await web3.action.disconnect();
});

export const checkIsConnected = createAsyncThunk("wallet/checkIsConnected", async (_, { dispatch }) => {
  const { data: isConnected } = await web3.action.isConnected();
  if (isConnected) {
    dispatch(getAccount());
  }

  return { isConnected };
});

export const getNetwork = createAsyncThunk("wallet/getNetwork", async (_) => {
  const data = await getNetworkRaw();

  return data;
});

const getNetworkRaw = async () => {
  const { data: networkName } = await web3.action.getNetwork();
  const { data: networkUrl } = await web3.action.getNetworkURL();
  const { data: networkNumber } = await web3.action.aptos.getChainId();

  let networkType = "aptos";
  if (web3.action.getNetworkType) {
    networkType = (await web3.action.getNetworkType()).data;
  }

  return {
    networkName,
    networkUrl,
    networkType,
    validNetwork: isCorrectBlockchain(networkType) && isCorrectNetwork(networkNumber, defaultConfigReducer),
  };
};
